import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import Swal from 'sweetalert2';
import {
  AlertTriangle,
  ArrowLeft,
  Banknote,
  Building2,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Ellipsis,
  FileText,
  Loader2,
  MapPinned,
  Receipt,
  RefreshCw,
  Route,
  Truck,
  Undo2,
  Wallet,
  Warehouse,
} from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { fetchGuideDetail, fetchGuides } from '@/services/guides.js';
import { fetchFacturaDeGuia } from '@/services/facturacion.js';
import { fetchGuideTariffSnapshots, fetchTariffWorkbench } from '@/services/tariffWorkbench.js';
import { previewRevalorizacionGuia, valorizarGuia } from '@/services/guiasValorizacion.js';
import { formatNumeroGuia } from '@/lib/guias/validation.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { GUIA_TIMELINE, CAMBIAR_ESTADO_GUIA } from '@/graphql/trazabilidadAlertas.gql.js';
import { downloadGuiaPdf, previewGuiaPdf, printGuiaPdf } from '@/modules/guias/services/printGuia.js';
import { useOperatorCompactQaScreen } from '@/utils/operatorCompactQa.js';
import { useOperatorCompactViewport } from '@/utils/operatorCompact.js';
import { hasPermission } from '@/utils/permissions.js';
import { RETORNABLES_MOVIMIENTOS_QUERY } from '@/graphql/retornables.gql.js';
import { isSameParty, parseDisplayDate, splitTransportFields, withEmissionEvent } from './guiaDetailView.js';

const TABS = [
  { id: 'historial', label: 'Historial' },
  { id: 'economico', label: 'Económico' },
  { id: 'observaciones', label: 'Observaciones' },
  { id: 'documentacion', label: 'Documentación' },
];

const TIMELINE_EVENT_META = {
  GUIA_EMITIDA: { label: 'Guía emitida', icon: FileText },
  GUIA_ESTADO_CAMBIADO: { label: 'Cambio de estado', icon: RefreshCw },
  AGREGADA_A_FOJA: { label: 'Enviada a foja', icon: ClipboardList },
  GUIA_EN_FOJA: { label: 'En foja', icon: ClipboardList },
  FOJA_CERRADA: { label: 'Foja cerrada', icon: ClipboardList },
  DESPACHO_CONFIRMADO: { label: 'Despacho confirmado', icon: Truck },
  GUIA_SCAN_CARGA: { label: 'Scan de carga', icon: Truck },
  GUIA_SCAN_DESCARGA: { label: 'Scan de descarga', icon: Building2 },
  GUIA_RECIBIDA_EN_VIA: { label: 'Recibida en vía', icon: Building2 },
  GUIA_PENDIENTE_RECANALIZACION: { label: 'Pendiente de recanalización', icon: Undo2 },
  RECIBIDA_DESTINO: { label: 'Recibida en destino', icon: Building2 },
  GUIA_RECIBIDA_EN_DESTINO: { label: 'Recibida en destino', icon: Building2 },
  GUIA_EN_REPARTO: { label: 'En reparto', icon: Truck },
  ASIGNADA_REPARTO: { label: 'Asignada a reparto', icon: Truck },
  ENTREGADA: { label: 'Entrega confirmada', icon: CheckCircle2 },
  RETENIDA: { label: 'Incidencia / retencion', icon: AlertTriangle },
  REPROGRAMADA: { label: 'Reprogramada', icon: Undo2 },
  COBRO_REGISTRADO: { label: 'Cobranza registrada', icon: Banknote },
  FACTURA_EMITIDA: { label: 'Factura emitida', icon: Receipt },
};

const TIMELINE_STEPS = [
  { id: 'emitida', label: 'Emitida', shortLabel: 'Emitida', states: ['EMITIDA', 'EN_FOJA', 'FOJA_CERRADA', 'EN_TRANSITO_A_DESTINO', 'EN_TRANSITO_A_VIA', 'RECIBIDA_EN_VIA', 'PENDIENTE_RECANALIZACION', 'RECIBIDA_DESTINO', 'EN_REPARTO', 'ENTREGADA', 'RETENIDA', 'REPROGRAMADA', 'ANULADA'] },
  { id: 'foja', label: 'En foja', shortLabel: 'Foja', states: ['EN_FOJA', 'FOJA_CERRADA', 'EN_TRANSITO_A_DESTINO', 'EN_TRANSITO_A_VIA', 'RECIBIDA_EN_VIA', 'PENDIENTE_RECANALIZACION', 'RECIBIDA_DESTINO', 'EN_REPARTO', 'ENTREGADA', 'RETENIDA', 'REPROGRAMADA'] },
  { id: 'transito', label: 'En tránsito', shortLabel: 'Tránsito', states: ['FOJA_CERRADA', 'EN_TRANSITO_A_DESTINO', 'EN_TRANSITO_A_VIA', 'RECIBIDA_EN_VIA', 'PENDIENTE_RECANALIZACION', 'RECIBIDA_DESTINO', 'EN_REPARTO', 'ENTREGADA', 'RETENIDA', 'REPROGRAMADA'] },
  { id: 'recepcion', label: 'Recibida', shortLabel: 'Recibida', states: ['RECIBIDA_DESTINO', 'EN_REPARTO', 'ENTREGADA', 'RETENIDA', 'REPROGRAMADA'] },
  { id: 'reparto', label: 'En reparto', shortLabel: 'Reparto', states: ['EN_REPARTO', 'ENTREGADA', 'RETENIDA', 'REPROGRAMADA'] },
  { id: 'entregada', label: 'Entregada', shortLabel: 'Entrega', states: ['ENTREGADA', 'RETENIDA', 'REPROGRAMADA', 'ANULADA'] },
];

const TIMELINE_STEP_ICONS = {
  emitida: FileText,
  foja: ClipboardList,
  transito: Route,
  recepcion: Warehouse,
  reparto: Truck,
  entregada: MapPinned,
};

function formatDate(value, options = {}) {
  if (!value) return 'Sin dato registrado';
  const date = options.dateOnly ? parseDisplayDate(value) : (value instanceof Date ? value : new Date(value));
  if (!date || Number.isNaN(date.getTime())) return 'Sin dato registrado';
  return new Intl.DateTimeFormat('es-AR', options.dateOnly
    ? { dateStyle: 'medium' }
    : { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function formatCompactDateTime(value) {
  if (!value) return 'Sin dato registrado';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin dato registrado';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month} ${hours}:${minutes}`;
}

function formatMoney(value) {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatNumber(value, digits = 0) {
  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value || 0));
}

function parseJsonSafe(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function normalizeState(value) {
  const normalized = String(value || '').trim().toUpperCase();
  return normalized || 'SIN_DATO';
}

function deriveRoleMode(user) {
  const haystack = [
    user?.role,
    ...(Array.isArray(user?.roles) ? user.roles : []),
    ...(Array.isArray(user?.permissionKeys) ? user.permissionKeys : []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/(cobranza|tesorer)/.test(haystack)) return 'cobranza';
  if (/(recepci|deposit|hub)/.test(haystack)) return 'recepcion';
  if (/(repart|trafic|despach)/.test(haystack)) return 'operacion';
  if (/(admin|geren|super)/.test(haystack)) return 'admin';
  return 'operacion';
}

function getCommercialStatus(guia) {
  const explicit = String(guia?.estado_comercial || '').trim().toUpperCase();
  if (explicit === 'PENDIENTE_VALORIZACION') {
    return { label: 'Pendiente de valorización', reason: 'La guía fue emitida sin valorizar y el circuito comercial sigue bloqueado.' };
  }
  const formaPago = String(guia?.modalidadCobro || guia?.formaPago || '').toUpperCase();
  if (!formaPago) return { label: 'No asignado', reason: 'Backend sin estado comercial canónico expuesto.' };
  if (/CONTADO/.test(formaPago)) return { label: 'Contado', reason: 'La cobranza queda operativamente condicionada a entrega.' };
  if (/CTA|CUENTA|CORRIENTE/.test(formaPago)) return { label: 'Cuenta corriente', reason: 'Se liquida contra cartera del cliente.' };
  return { label: formaPago, reason: 'Condición persistida al emitir.' };
}

function getBillingStatus(guia) {
  if (String(guia?.estado_facturacion || '').trim().toUpperCase() === 'PENDIENTE_VALORIZACION') {
    return { label: 'Bloqueada', reason: 'No se puede facturar hasta valorizar la guía.' };
  }
  if (guia?.facturada) return { label: 'Facturada', reason: 'La guía ya impactó en facturación.' };
  if (Number(guia?.total_valorizado || 0) <= 0) return { label: 'Sin valorización', reason: 'No se debe facturar sin snapshot valorizado.' };
  return { label: 'Pendiente', reason: 'Lista para facturar cuando el circuito comercial lo habilite.' };
}

function getCollectionStatus(guia, operationalState) {
  if (String(guia?.estado_cobranza || guia?.estado_comercial || '').trim().toUpperCase() === 'PENDIENTE_VALORIZACION') {
    return { label: 'Bloqueada', reason: 'La cobranza queda bloqueada hasta valorizar la guía.' };
  }
  const formaPago = String(guia?.modalidadCobro || guia?.formaPago || '').toUpperCase();
  if (!formaPago) return { label: 'No asignado', reason: 'Estado de cobranza canónico aún no expuesto.' };
  if (!/CONTADO/.test(formaPago)) return { label: 'No inmediata', reason: 'La condición de pago no requiere cobranza al acto.' };
  if (operationalState !== 'ENTREGADA') return { label: 'Bloqueada', reason: 'No cobrar sin entrega confirmada.' };
  return { label: 'Habilitada', reason: 'La entrega ya quedo confirmada.' };
}

function getStatusTone(state) {
  const normalized = normalizeState(state);
  if (['ENTREGADA'].includes(normalized)) return 'success';
  if (['RETENIDA', 'ANULADA', 'PENDIENTE_RECANALIZACION'].includes(normalized)) return 'danger';
  if (['RECIBIDA_DESTINO', 'REPROGRAMADA'].includes(normalized)) return 'warning';
  return 'default';
}

function flattenValuationRows(snapshot, guia) {
  const items = Array.isArray(snapshot?.items)
    ? snapshot.items
    : Array.isArray(snapshot?.conceptos)
      ? snapshot.conceptos
      : [];
  const decisionOrigin = String(snapshot?.valuation_decision?.origin || '').trim().toLowerCase();
  const pendingValuation = Boolean(snapshot?.valuation_decision?.pending_valuation) || decisionOrigin === 'sin_valorizar';

  if (items.length) {
    return items.map((item, index) => ({
      id: item?.conceptoId || item?.codigo || `concepto-${index + 1}`,
      concepto: item?.nombre || item?.codigo || `Concepto ${index + 1}`,
      base: item?.base ?? item?.aplicaSobre ?? item?.cantidad ?? 'Persistido',
      calculo: item?.calculo ?? item?.tipo ?? item?.valor ?? 'Snapshot',
      importe: Number(item?.importe || 0),
    }));
  }

  return [{
    id: 'snapshot-total',
    concepto: pendingValuation ? 'Pendiente de valorización' : 'Valorización congelada',
    base: 'Emisión',
    calculo: pendingValuation
      ? 'Emitida sin valorizar'
      : guia?.valorizacion_snapshot_at
        ? `Congelada ${formatDate(guia.valorizacion_snapshot_at)}`
        : 'Persistida',
    importe: Number(guia?.total_valorizado || guia?.total || 0),
  }];
}

function flattenRelationalValuationRows(snapshot) {
  const details = Array.isArray(snapshot?.details) ? snapshot.details : [];
  return details.map((item, index) => ({
    id: item?.id || `${item?.conceptoCodigo || 'concepto'}-${index + 1}`,
    concepto: item?.conceptoNombre || item?.conceptoCodigo || `Concepto ${index + 1}`,
    base: item?.baseDescripcion || item?.baseCodigo || 'Persistido',
    calculo: item?.tipoCalculo || item?.reglaCodigo || 'Snapshot relacional',
    importe: Number(item?.importe || 0),
  }));
}

function flattenPersistedValuationRows(snapshot, guia) {
  const details = Array.isArray(snapshot?.resolvedDetail) ? snapshot.resolvedDetail : [];
  if (details.length) {
    return details.map((item, index) => ({
      id: item?.id || `${item?.conceptoCodigo || 'concepto'}-${index + 1}`,
      concepto: item?.conceptoNombre || item?.conceptoCodigo || `Concepto ${index + 1}`,
      base: item?.cantidad ?? 'Persistido',
      calculo: item?.origen || snapshot?.source || 'Snapshot persistido',
      importe: Number(item?.importe || 0),
    }));
  }

  return flattenValuationRows(parseJsonSafe(guia?.detalle_valorizacion_json), guia);
}

function formatSnapshotSource(value) {
  const normalized = String(value || '').trim().toUpperCase();
  if (!normalized) return 'No asignado';
  if (normalized === 'EMISION') return 'Emisión';
  if (normalized === 'REVALORIZACION') return 'Revalorización';
  if (normalized === 'MANUAL') return 'Ajuste manual';
  if (normalized === 'OVERRIDE') return 'Override';
  if (normalized === 'DETALLE') return 'Snapshot detalle';
  if (normalized === 'CONCEPTOS_JSON') return 'Conceptos JSON';
  if (normalized === 'GUIA_VALORIZACION') return 'Cabecera legacy';
  return normalized;
}

function buildRevaluationHistory(headers = []) {
  return headers.map((header, index) => {
    const previous = headers[index + 1] || null;
    const previousTotal = Number(previous?.totalFinal || 0);
    const newTotal = Number(header?.totalFinal || 0);
    return {
      id: header.id,
      fecha: header.createdAt,
      usuario: header.createdBy ? `#${header.createdBy}` : 'Sistema',
      motivo: header.motivo || 'Sin motivo informado',
      source: formatSnapshotSource(header.source),
      previousTotal,
      newTotal,
      delta: Number((newTotal - previousTotal).toFixed(2)),
      details: Array.isArray(header.details) ? flattenRelationalValuationRows(header) : [],
    };
  });
}

function RevalorizacionDrawer({
  open,
  onClose,
  guia,
  snapshotBundle,
  tenant,
  token,
  onApplied,
}) {
  const [loadingTariffs, setLoadingTariffs] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [motivo, setMotivo] = useState('');
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [tariffOptions, setTariffOptions] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open) {
      setMotivo('');
      setSelectedVersionId('');
      setPreview(null);
      setError('');
      return;
    }

    let active = true;
    setLoadingTariffs(true);
    fetchTariffWorkbench({ tenant, token, filters: { activo: true, estado: 'VIGENTE' } })
      .then((payload) => {
        if (!active) return;
        const options = (payload?.listas || [])
          .filter((item) => item?.versionActualId)
          .map((item) => ({
            id: String(item.versionActualId),
            label: `${item.codigo || 'LISTA'} · ${item.nombre || 'Sin nombre'} · v${item.versionActualNumero || item.versionActualId}`,
          }));
        setTariffOptions(options);
      })
      .catch(() => {
        if (active) setTariffOptions([]);
      })
      .finally(() => {
        if (active) setLoadingTariffs(false);
      });

    return () => { active = false; };
  }, [open, tenant, token]);

  const currentRows = useMemo(() => {
    if (snapshotBundle?.current?.details?.length) return flattenRelationalValuationRows(snapshotBundle.current);
    return flattenPersistedValuationRows(guia?.valorizacion_snapshot, guia);
  }, [guia, snapshotBundle]);

  const currentTotal = Number(guia?.valorizacion_snapshot?.header?.totalFinal || snapshotBundle?.current?.totalFinal || guia?.total_valorizado || guia?.total || 0);
  const previewRows = useMemo(() => (preview?.simulation?.detalles || []).map((item, index) => ({
    id: item.ruleId || item.conceptId || `preview-${index + 1}`,
    concepto: item.conceptName || item.conceptCode || `Concepto ${index + 1}`,
    base: item.baseDescripcion || item.baseCodigo || 'Persistido',
    calculo: item.ruleCode || item.metodoCalculo || 'Regla',
    importe: Number(item.importe || 0),
  })), [preview]);

  const handlePreview = useCallback(async () => {
    if (!guia?.id) return;
    setPreviewing(true);
    setError('');
    try {
      const data = await previewRevalorizacionGuia({
        tenant,
        token,
        guiaId: guia.id,
        payload: {
          tipo: 'auto',
          versionId: selectedVersionId || undefined,
        },
      });
      setPreview(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo obtener el preview.');
    } finally {
      setPreviewing(false);
    }
  }, [guia?.id, selectedVersionId, tenant, token]);

  const handleConfirm = useCallback(async () => {
    if (!guia?.id) return;
    if (!motivo.trim()) {
      setError('El motivo de revalorización es obligatorio.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await valorizarGuia({
        tenant,
        token,
        guiaId: guia.id,
        payload: {
          tipo: 'auto',
          versionId: selectedVersionId || undefined,
          motivo: motivo.trim(),
        },
      });
      onApplied?.();
      onClose?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo revalorizar la guía.');
    } finally {
      setSaving(false);
    }
  }, [guia?.id, motivo, onApplied, onClose, selectedVersionId, tenant, token]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-[rgba(15,23,42,0.42)]">
      <div className="flex h-full w-full max-w-[760px] flex-col overflow-hidden border-l border-[rgba(var(--border),0.7)] bg-[rgb(var(--panel))] shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-[rgba(var(--border),0.6)] px-5 py-4">
          <div>
            <div className="text-sm font-semibold text-[rgb(var(--fg))]">Revalorizar guía</div>
            <div className="mt-1 text-xs text-[rgba(var(--fg),0.62)]">ST338 moderno: recalcula conceptos, publica nuevo snapshot y deja trazabilidad.</div>
          </div>
          <Button type="button" variant="ghost" onClick={onClose} className="rounded-xl">Cerrar</Button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className="grid gap-3 md:grid-cols-3">
            <MiniStat label="Total actual" value={formatMoney(currentTotal)} icon={Receipt} />
            <MiniStat label="Snapshot vigente" value={formatSnapshotSource(snapshotBundle?.current?.source)} helper={formatDate(snapshotBundle?.current?.createdAt || guia?.valorizacion_snapshot_at)} icon={Clock3} />
            <MiniStat label="Estado de facturación" value={guia?.facturada ? 'Facturada' : 'Pendiente'} icon={FileText} />
          </div>

          <Surface>
            <div className="space-y-3 p-4">
              <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                <label className="text-sm">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[rgba(var(--fg),0.56)]">Tarifa / version a aplicar</span>
                  <select value={selectedVersionId} onChange={(event) => setSelectedVersionId(event.target.value)} className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-transparent px-3 py-2 text-sm">
                    <option value="">Resolver automaticamente</option>
                    {tariffOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                  </select>
                </label>
                <div className="flex items-end">
                  <Button type="button" onClick={handlePreview} disabled={previewing || loadingTariffs} className="rounded-xl">
                    {previewing ? 'Calculando...' : 'Previsualizar'}
                  </Button>
                </div>
              </div>
              <label className="text-sm">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[rgba(var(--fg),0.56)]">Motivo obligatorio</span>
                <textarea value={motivo} onChange={(event) => setMotivo(event.target.value)} rows={3} className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-transparent px-3 py-2 text-sm" placeholder="Reclamo, excepción operativa, cambio de tarifa comercial, corrección previa a facturar..." />
              </label>
            </div>
          </Surface>

          <div className="grid gap-4 xl:grid-cols-2">
            <Surface>
              <div className="p-4">
                <div className="mb-3 text-sm font-semibold text-[rgb(var(--fg))]">Valorizacion actual por concepto</div>
                <div className="overflow-hidden rounded-2xl border border-[rgba(var(--border),0.6)]">
                  <table className="min-w-full border-collapse text-sm">
                    <thead className="bg-[rgba(var(--subpanel),0.92)] text-left text-[11px] uppercase tracking-[0.14em] text-[rgba(var(--fg),0.56)]">
                      <tr><th className="px-3 py-2.5">Concepto</th><th className="px-3 py-2.5">Base</th><th className="px-3 py-2.5 text-right">Importe</th></tr>
                    </thead>
                    <tbody>{currentRows.map((row) => <tr key={row.id} className="border-t border-[rgba(var(--border),0.4)]"><td className="px-3 py-2.5">{row.concepto}</td><td className="px-3 py-2.5 text-[rgba(var(--fg),0.72)]">{row.base}</td><td className="px-3 py-2.5 text-right font-semibold">{formatMoney(row.importe)}</td></tr>)}</tbody>
                  </table>
                </div>
              </div>
            </Surface>

            <Surface>
              <div className="p-4">
                <div className="mb-3 text-sm font-semibold text-[rgb(var(--fg))]">Preview nuevo snapshot</div>
                {preview ? (
                  <>
                    <div className="grid gap-2 md:grid-cols-3">
                      <MiniStat label="Total anterior" value={formatMoney(preview.diff?.previousTotal)} />
                      <MiniStat label="Total nuevo" value={formatMoney(preview.diff?.nextTotal)} />
                      <MiniStat label="Diferencia" value={formatMoney(preview.diff?.deltaTotal)} />
                    </div>
                    <div className="mt-4 overflow-hidden rounded-2xl border border-[rgba(var(--border),0.6)]">
                      <table className="min-w-full border-collapse text-sm">
                        <thead className="bg-[rgba(var(--subpanel),0.92)] text-left text-[11px] uppercase tracking-[0.14em] text-[rgba(var(--fg),0.56)]">
                          <tr><th className="px-3 py-2.5">Concepto</th><th className="px-3 py-2.5">Cálculo</th><th className="px-3 py-2.5 text-right">Importe</th></tr>
                        </thead>
                        <tbody>{previewRows.map((row) => <tr key={row.id} className="border-t border-[rgba(var(--border),0.4)]"><td className="px-3 py-2.5">{row.concepto}</td><td className="px-3 py-2.5 text-[rgba(var(--fg),0.72)]">{row.calculo}</td><td className="px-3 py-2.5 text-right font-semibold">{formatMoney(row.importe)}</td></tr>)}</tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="rounded-2xl border border-dashed border-[rgba(var(--border),0.6)] p-4 text-sm text-[rgba(var(--fg),0.62)]">Ejecutá la previsualizacion para comparar total anterior, total nuevo y detalle por concepto.</div>
                )}
              </div>
            </Surface>
          </div>

          {error ? <div className="rounded-2xl border border-[rgba(var(--danger),0.35)] bg-[rgba(var(--danger),0.1)] p-3 text-sm text-[rgb(var(--danger))]">{error}</div> : null}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-[rgba(var(--border),0.6)] px-5 py-4">
          <div className="text-xs text-[rgba(var(--fg),0.58)]">Facturación seguirá leyendo el snapshot persistido vigente, no un cálculo efímero.</div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">Cancelar</Button>
            <Button type="button" onClick={handleConfirm} disabled={saving || !preview} className="rounded-xl">
              {saving ? 'Aplicando...' : 'Confirmar revalorización'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function inferRouteLabel(guia) {
  const origin = guia?.sucursalOrigen?.nombre || 'Origen';
  const destination = guia?.sucursalDestino?.nombre || 'Destino';
  return `${origin} → ${destination}`;
}

function getNextActionHint(guia, state) {
  const actionMap = {
    EMITIDA: { label: 'Enviar a foja', description: 'La guía está emitida y el siguiente paso válido es incorporarla a una foja.', actionKey: 'foja' },
    EN_FOJA: { label: 'Esperando cierre de foja', description: 'La guía ya está consolidada. El siguiente hito operativo depende del cierre de foja.', actionKey: null },
    FOJA_CERRADA: { label: 'Pendiente de recepción', description: 'La carga ya salió. El próximo hito operativo es la recepción formal en destino o vía.', actionKey: null },
    EN_TRANSITO_A_DESTINO: { label: 'Pendiente de recepción', description: 'La guía está en tránsito a la sucursal operativa de destino.', actionKey: null },
    EN_TRANSITO_A_VIA: { label: 'Pendiente de recepción en vía', description: 'La guía viaja a un punto intermedio y todavía no puede salir a reparto.', actionKey: null },
    RECIBIDA_EN_VIA: { label: 'Enviar a foja de continuidad', description: 'La guía llegó a vía. Debe recanalizarse para seguir su tramo.', actionKey: 'foja' },
    PENDIENTE_RECANALIZACION: { label: 'Recanalizar', description: 'La guía quedó pendiente de continuidad. No corresponde reparto directo.', actionKey: 'foja' },
    RECIBIDA_DESTINO: { label: 'Asignar a reparto', description: 'La guía ya fue recibida y puede pasar a última milla.', actionKey: 'reparto' },
    EN_REPARTO: { label: 'Registrar resultado', description: 'Hace falta cerrar el reparto con entrega o incidencia.', actionKey: 'confirmar' },
    RETENIDA: { label: 'Gestionar incidencia', description: 'La guía sigue retenida. Si la incidencia se resolvió, puede reabrirse.', actionKey: 'reabrir' },
    REPROGRAMADA: { label: 'Reintentar reparto', description: 'La reprogramación ya quedó registrada. El próximo paso es volver a reparto.', actionKey: 'reabrir' },
    ENTREGADA: { label: 'Registrar cobranza', description: 'El flujo logístico cerró. Si la condición lo exige, continúa cobranza.', actionKey: 'cobranza' },
    ANULADA: { label: 'Sin acción operativa', description: 'La guía quedó anulada y solo debe conservarse para consulta y auditoría.', actionKey: null },
  };

  return actionMap[state] || {
    label: 'Revisar consistencia',
    description: guia ? 'El estado no tiene recomendación mapeada. Validar datos persistidos.' : 'Sin guía cargada.',
    actionKey: null,
  };
}

function normalizeEntityText(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  return /^(?:id|#)\s*\S+$/i.test(text) || /^\d+$/.test(text) ? '' : text;
}

function hasMeaningfulText(value) {
  return normalizeEntityText(value) !== '';
}

function pickFirstMeaningful(...values) {
  return values.map(normalizeEntityText).find(Boolean) || '';
}

// Numeric values (bultos, kg, importes) are valid display values; only entity names reject bare ids.
function hasDisplayValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

const PAYMENT_CONDITION_LABELS = {
  CONTADO_ORIGEN: 'Contado origen',
  CONTADO_DESTINO: 'Contado destino',
  CTA_CTE_ORIGEN: 'Cuenta corriente origen',
  CTA_CTE_DESTINO: 'Cuenta corriente destino',
  CUENTA_CORRIENTE: 'Cuenta corriente',
  SIN_CARGO: 'Sin cargo',
  ORIGEN: 'Paga origen',
  DESTINO: 'Paga destino',
};

function formatPaymentCondition(value) {
  const key = String(value || '').trim().toUpperCase();
  if (!key) return '';
  return PAYMENT_CONDITION_LABELS[key] || key.replaceAll('_', ' ').toLowerCase().replace(/^\w/, (char) => char.toUpperCase());
}

function joinParts(...parts) {
  return parts.map((part) => String(part ?? '').trim()).filter((part) => part && part !== 'No asignado').join(' · ');
}

function getEntityId(entity, fallbackId = null) {
  if (entity && typeof entity === 'object') {
    const candidates = [entity.id, entity.cliente_id, entity.persona_id, entity.entidad_id];
    const found = candidates.find((value) => value !== null && value !== undefined && value !== '');
    if (found !== undefined) return found;
  }
  return fallbackId;
}

function getEntityDisplayName(entidad) {
  if (entidad == null) return '';
  if (typeof entidad === 'string') return normalizeEntityText(entidad);

  const candidates = [
    entidad.razon_social,
    entidad.razonSocial,
    entidad.nombre,
    entidad.apellido_nombre,
    entidad.nombre_fantasia,
    entidad.nombreFantasia,
    entidad.cliente_nombre,
    entidad.denominacion,
    entidad.descripcion,
    entidad.label,
  ];

  return pickFirstMeaningful(...candidates);
}

function buildEntityDisplay(entidad, fallbackId = null, fallbackLabel = 'Sin dato registrado') {
  const entityId = getEntityId(entidad, fallbackId);
  const principal = getEntityDisplayName(entidad);
  const safePrincipal = principal || fallbackLabel;
  const showIdHelper = entityId !== null && entityId !== undefined && entityId !== '' && hasMeaningfulText(safePrincipal);
  return {
    id: entityId,
    primary: safePrincipal || fallbackLabel,
    secondary: showIdHelper ? `ID ${entityId}` : null,
  };
}

function getClientName(guia) {
  const entityId = guia?.cliente_id ?? guia?.pagador_cliente_id ?? guia?.cliente?.id ?? guia?.pagador?.id ?? null;
  const primary = pickFirstMeaningful(
    guia?.cliente_nombre,
    guia?.cliente_razon_social,
    guia?.cliente_nombre_fantasia,
    guia?.cliente?.cliente_nombre,
    guia?.cliente?.nombre_fantasia,
    guia?.cliente?.razon_social,
    guia?.cliente?.nombre,
    guia?.cliente?.denominacion,
    guia?.pagador_nombre,
    guia?.pagador?.nombre,
    guia?.pagador?.razon_social,
    guia?.pagador?.nombre_fantasia,
  );
  return {
    id: entityId,
    primary: primary || 'Sin dato registrado',
    secondary: entityId !== null && entityId !== undefined && entityId !== '' ? `ID ${entityId}` : null,
  };
}

function formatMetricValue(value, formatter) {
  if (value === null || value === undefined || value === '') return 'Sin dato registrado';
  return formatter ? formatter(value) : value;
}

function formatOptionalValue(value, fallback = 'Sin dato registrado') {
  const text = normalizeEntityText(value);
  return text || fallback;
}

function formatLineaOperativa({ sucursalCodigo = '', lineaNumero = '' } = {}) {
  const codigo = String(sucursalCodigo ?? '').trim();
  const lineaRaw = String(lineaNumero ?? '').trim();
  if (!codigo && !lineaRaw) return '';

  const digits = lineaRaw.replace(/\D/g, '');
  const lineaFormateada = digits ? digits.padStart(8, '0') : lineaRaw;
  return codigo ? `${codigo}-${lineaFormateada}` : lineaFormateada;
}

function getTransportDisplay(guia) {
  const lineaOperativa = pickFirstMeaningful(guia?.linea_operativa, guia?.linea_operativa_codigo) || formatLineaOperativa({
    sucursalCodigo: String(
      guia?.sucursal_origen_codigo
      || guia?.sucursalOrigen?.codigo
      || guia?.sucursal_actual_codigo
      || guia?.sucursalActual?.codigo
      || guia?.codigo_sucursal
      || guia?.origen_codigo
      || guia?.sucursalCodigo
      || guia?.sucursal_origen_id
      || guia?.sucursal_actual_id
      || '',
    ).trim(),
    lineaNumero: String(
      guia?.linea_numero
      || guia?.linea
      || guia?.linea_id
      || '',
    ).trim(),
  });

  return {
    linea: lineaOperativa || pickFirstMeaningful(guia?.linea_operativa, guia?.linea_nombre, guia?.linea?.nombre, guia?.linea?.codigo, guia?.linea_numero, guia?.linea),
    foja: guia?.foja_id ? `#${guia.foja_id}` : '',
    chofer: pickFirstMeaningful(guia?.chofer?.nombre, guia?.chofer?.apellido_nombre, guia?.chofer_nombre, guia?.choferNombre),
    unidad: pickFirstMeaningful(guia?.unidad?.patente, guia?.unidad?.dominio, guia?.vehiculo?.patente, guia?.vehiculo?.dominio, guia?.unidad_patente, guia?.patente, guia?.dominio),
    origen: pickFirstMeaningful(guia?.sucursalOrigen?.nombre, guia?.sucursal_origen?.nombre, guia?.origen?.nombre, guia?.origen, guia?.sucursalOrigen?.descripcion),
    destino: pickFirstMeaningful(guia?.sucursalDestino?.nombre, guia?.sucursal_destino?.nombre, guia?.destino?.nombre, guia?.destino, guia?.sucursalDestino?.descripcion),
  };
}

function resolveTimelineStepFromEvent(event) {
  const tipo = normalizeState(event?.tipo);
  const estado = normalizeState(event?.estado || event?.estado_nuevo);

  if (['GUIA_EMITIDA', 'EMITIDA'].includes(tipo) || estado === 'EMITIDA') return 'emitida';
  if (['AGREGADA_A_FOJA', 'GUIA_EN_FOJA'].includes(tipo) || estado === 'EN_FOJA') return 'foja';
  if (['FOJA_CERRADA', 'DESPACHO_CONFIRMADO', 'GUIA_SCAN_CARGA'].includes(tipo) || ['FOJA_CERRADA', 'EN_TRANSITO_A_DESTINO', 'EN_TRANSITO_A_VIA'].includes(estado)) return 'transito';
  if (['GUIA_RECIBIDA_EN_VIA', 'RECIBIDA_DESTINO', 'GUIA_RECIBIDA_EN_DESTINO', 'GUIA_SCAN_DESCARGA'].includes(tipo) || ['RECIBIDA_EN_VIA', 'RECIBIDA_DESTINO', 'PENDIENTE_RECANALIZACION'].includes(estado)) return 'recepcion';
  if (['GUIA_EN_REPARTO', 'ASIGNADA_REPARTO'].includes(tipo) || estado === 'EN_REPARTO') return 'reparto';
  if (tipo === 'ENTREGADA' || ['ENTREGADA', 'RETENIDA', 'REPROGRAMADA', 'ANULADA'].includes(estado)) return 'entregada';
  return null;
}

function buildActionModel({ guia, state, nextAction, collectionStatus }) {
  const actionMap = {
    foja: {
      key: 'foja',
      label: 'Enviar a foja',
      enabled: ['EMITIDA', 'RECIBIDA_EN_VIA', 'PENDIENTE_RECANALIZACION'].includes(state) && !guia?.foja_id,
      reason: guia?.foja_id ? `La guía ya está asociada a foja #${guia.foja_id}.` : 'La guía está lista para foja o continuidad.',
    },
    reparto: {
      key: 'reparto',
      label: 'Asignar a reparto',
      enabled: state === 'RECIBIDA_DESTINO' && !guia?.reparto_id,
      reason: guia?.reparto_id ? `La guía ya está en reparto #${guia.reparto_id}.` : 'Solo corresponde luego de recepción en destino.',
    },
    confirmar: {
      key: 'confirmar',
      label: 'Registrar entrega',
      enabled: state === 'EN_REPARTO',
      reason: 'La entrega solo puede confirmarse cuando la guía está en reparto.',
    },
    reabrir: {
      key: 'reabrir',
      label: state === 'REPROGRAMADA' ? 'Reintentar reparto' : 'Reabrir guía',
      enabled: state === 'RETENIDA' || state === 'REPROGRAMADA',
      reason: 'Solo se reabre una guía retenida o reprogramada.',
    },
    cobranza: {
      key: 'cobranza',
      label: 'Registrar cobranza',
      enabled: state === 'ENTREGADA' && collectionStatus.label === 'Habilitada',
      reason: 'Cobranza disponible luego de entrega cuando la condición comercial lo exige.',
    },
  };

  const primaryAction = nextAction?.actionKey ? actionMap[nextAction.actionKey] || null : null;
  const secondaryActions = [
    { key: 'refresh', label: 'Refrescar', enabled: true, reason: 'Recargar datos de la guía.' },
    { key: 'etiquetas', label: 'Etiquetas de bultos', enabled: true, reason: 'Ver, imprimir o reimprimir las etiquetas asociadas a la guía.' },
    { key: 'incidencia', label: 'Registrar incidencia', enabled: !['ANULADA', 'ENTREGADA'].includes(state), reason: 'Permite documentar retencion, reprogramacion u otra novedad.' },
    { key: 'anular', label: 'Anular', enabled: !['ENTREGADA', 'ANULADA'].includes(state), reason: 'Anula la guía dejando trazabilidad y motivo auditado.' },
  ];

  return { primaryAction, secondaryActions };
}

function buildOperationalAlerts({ guia, state, billingStatus, collectionStatus, timelinePreview }) {
  const alerts = [];

  if (['FOJA_CERRADA', 'EN_TRANSITO_A_DESTINO', 'EN_TRANSITO_A_VIA'].includes(state)) {
    alerts.push({
      id: 'recepcion',
      tone: 'warning',
      label: 'Pendiente de recepción',
      detail: 'La guía ya salió y espera recepción formal en el punto operativo correspondiente.',
    });
  }

  if (state === 'PENDIENTE_RECANALIZACION' || state === 'RECIBIDA_EN_VIA') {
    alerts.push({
      id: 'recanalizacion',
      tone: 'danger',
      label: 'Recanalizacion pendiente',
      detail: 'La guía llegó a vía y todavía no tiene continuidad operativa resuelta.',
    });
  }

  if (state === 'RETENIDA') {
    alerts.push({
      id: 'retenida',
      tone: 'danger',
      label: 'Guia retenida',
      detail: timelinePreview?.descripcion || 'Existe una incidencia operativa que bloquea el cierre de la guía.',
    });
  }

  if (state === 'REPROGRAMADA') {
    alerts.push({
      id: 'reprogramada',
      tone: 'warning',
      label: 'Entrega reprogramada',
      detail: 'La guía requiere un nuevo intento o nueva coordinación de reparto.',
    });
  }

  if (collectionStatus.label === 'Habilitada') {
    alerts.push({
      id: 'cobranza',
      tone: 'warning',
      label: 'Pendiente de cobranza',
      detail: 'La entrega ya quedó confirmada y la condición comercial permite cobrar.',
    });
  }

  if (billingStatus.label === 'Pendiente') {
    alerts.push({
      id: 'facturacion',
      tone: 'default',
      label: 'Pendiente de facturación',
      detail: 'La guía conserva valorización y todavía no impactó en facturación.',
    });
  }

  if (guia?.observaciones) {
    alerts.push({
      id: 'observaciones',
      tone: 'default',
      label: 'Observaciones',
      detail: String(guia.observaciones).slice(0, 140),
    });
  }

  return alerts;
}

function getCurrentTimelineStepId(operationalState) {
  const currentIndex = TIMELINE_STEPS.findIndex((step, index) => step.states.includes(operationalState) && (TIMELINE_STEPS[index + 1] ? !TIMELINE_STEPS[index + 1].states.includes(operationalState) : true));
  return currentIndex >= 0 ? TIMELINE_STEPS[currentIndex]?.id || TIMELINE_STEPS[0].id : TIMELINE_STEPS[0].id;
}

function getCurrentStateStartedAt({ guia, operationalState, timelinePreview }) {
  const currentStepId = getCurrentTimelineStepId(operationalState);
  return timelinePreview?.stepMoments?.[currentStepId]
    || timelinePreview?.latestEvent?.created_at
    || guia?.updated_at
    || guia?.fechaEmision
    || guia?.fecha_emision
    || null;
}

function formatElapsedTime(value) {
  if (!value) return 'No asignado';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'No asignado';

  const diffMs = Math.max(Date.now() - date.getTime(), 0);
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${Math.max(minutes, 1)}m`;
}

function getOperationalSlaModel(operationalState, startedAt) {
  const targetsByState = {
    EMITIDA: 4,
    EN_FOJA: 8,
    FOJA_CERRADA: 24,
    EN_TRANSITO_A_DESTINO: 24,
    EN_TRANSITO_A_VIA: 18,
    RECIBIDA_EN_VIA: 6,
    PENDIENTE_RECANALIZACION: 6,
    RECIBIDA_DESTINO: 8,
    EN_REPARTO: 12,
    RETENIDA: 12,
    REPROGRAMADA: 24,
  };

  const targetHours = targetsByState[operationalState];
  if (!targetHours || !startedAt) {
    return {
      label: 'No asignado',
      tone: 'default',
      targetLabel: 'SLA no parametrizado',
      elapsedLabel: formatElapsedTime(startedAt),
    };
  }

  const date = new Date(startedAt);
  if (Number.isNaN(date.getTime())) {
    return {
      label: 'No asignado',
      tone: 'default',
      targetLabel: `${targetHours}h`,
      elapsedLabel: 'No asignado',
    };
  }

  const elapsedHours = (Date.now() - date.getTime()) / 3600000;
  if (elapsedHours >= targetHours) {
    return { label: 'Vencido', tone: 'danger', targetLabel: `${targetHours}h`, elapsedLabel: formatElapsedTime(startedAt) };
  }
  if (elapsedHours >= targetHours * 0.75) {
    return { label: 'En riesgo', tone: 'warning', targetLabel: `${targetHours}h`, elapsedLabel: formatElapsedTime(startedAt) };
  }
  return { label: 'En termino', tone: 'success', targetLabel: `${targetHours}h`, elapsedLabel: formatElapsedTime(startedAt) };
}

function getToneClasses(tone = 'default') {
  if (tone === 'danger') return 'border-[rgba(var(--danger),0.28)] bg-[rgba(var(--danger),0.12)] text-[rgb(var(--danger))]';
  if (tone === 'warning') return 'border-[rgba(var(--warning),0.28)] bg-[rgba(var(--warning),0.14)] text-[rgb(var(--warning))]';
  if (tone === 'success') return 'border-[rgba(var(--success),0.28)] bg-[rgba(var(--success),0.14)] text-[rgb(var(--success))]';
  return 'border-[rgba(var(--brand),0.22)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--fg))]';
}

function Surface({ className = '', children }) {
  return <section className={`rounded-2xl border border-[rgba(var(--border),0.68)] bg-[rgba(var(--panel),0.92)] shadow-[0_12px_30px_rgba(var(--shadow),0.12)] ${className}`}>{children}</section>;
}

function StatusBadge({ label }) {
  return <Badge variant={getStatusTone(label)} className="rounded-full px-3 py-1 text-[11px] tracking-[0.16em]">{String(label || 'No asignado').replaceAll('_', ' ')}</Badge>;
}

function MiniStat({ label, value, helper = null, icon: Icon = null, compact = false, dense = false }) {
  return (
    <div className={`rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.8)] ${dense ? 'px-2 py-1.5' : compact ? 'px-2.5 py-1.5' : 'px-3 py-2.5'}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[rgba(var(--fg),0.56)]">
        {Icon ? <Icon size={14} aria-hidden="true" /> : null}
        <span>{label}</span>
      </div>
      <div className={`${dense ? 'mt-0.5 text-[13px]' : 'mt-1 text-sm'} font-semibold text-[rgb(var(--fg))]`}>{value}</div>
      {helper ? <div className="mt-0.5 text-xs text-[rgba(var(--fg),0.6)]">{helper}</div> : null}
    </div>
  );
}

function CompactRow({ label, value, strong = false, compact = false, dense = false, helper = null, hideWhenEmpty = false }) {
  const hasValue = hasDisplayValue(value);
  if (hideWhenEmpty && !hasValue) return null;
  return (
    <div className={`flex items-start justify-between gap-4 border-b border-[rgba(var(--border),0.4)] ${dense ? 'py-1.5' : compact ? 'py-1.5' : 'py-2.5'} last:border-b-0`}>
      <span className="shrink-0 text-[11px] uppercase tracking-[0.14em] text-[rgba(var(--fg),0.56)]">{label}</span>
      <span className="min-w-0 text-right">
        <span className={`block ${dense ? 'text-[13px]' : 'text-sm'} ${strong ? 'font-semibold text-[rgb(var(--fg))]' : 'text-[rgba(var(--fg),0.76)]'}`}>{hasValue ? value : 'Sin dato registrado'}</span>
        {helper ? <span className="block text-[11px] leading-4 text-[rgba(var(--fg),0.56)]">{helper}</span> : null}
      </span>
    </div>
  );
}

function TabButton({ active, children, onClick, compact = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={`rounded-full border font-semibold transition ${compact ? 'px-2.5 py-0.5 text-[11px]' : 'px-3 py-1.5 text-xs'} ${active ? 'border-[rgba(var(--brand),0.75)] bg-[rgba(var(--brand),0.16)] text-[rgb(var(--fg))] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]' : 'border-transparent bg-transparent text-[rgba(var(--fg),0.64)] hover:border-[rgba(var(--border),0.48)] hover:bg-[rgba(var(--subpanel),0.7)]'}`}
    >
      {children}
    </button>
  );
}

function QuickActionButton({ label, onClick, icon: Icon, variant = 'outline', compact = false }) {
  return (
    <Button type="button" variant={variant} size="sm" onClick={onClick} className={`${compact ? 'h-8 px-2.5' : 'h-9 px-3'} rounded-xl`} aria-label={label}>
      {Icon ? <Icon size={15} aria-hidden="true" /> : null}
      <span className={compact ? 'hidden lg:inline' : 'hidden sm:inline'}>{label}</span>
    </Button>
  );
}

function MoreActionsMenu({ compact = false, actions, onAction, onDownloadPdf, onViewHistory, canRevalorizar, onOpenRevalorizar }) {
  const buttonClassName = `flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.9)] disabled:cursor-not-allowed disabled:opacity-45`;

  const handleSelect = (callback, enabled = true) => {
    if (!enabled) return;
    callback?.();
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  return (
    <details className="relative">
      <summary className={`flex cursor-pointer list-none items-center gap-2 rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--subpanel),0.58)] ${compact ? 'h-8 px-2.5 text-xs' : 'h-9 px-3 text-sm'} font-semibold text-[rgb(var(--fg))]`}>
        <Ellipsis size={15} aria-hidden="true" />
        <span className={compact ? 'hidden xl:inline' : 'hidden sm:inline'}>Más acciones</span>
      </summary>
      <div className="absolute right-0 top-full z-30 mt-2 w-64 rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgb(var(--panel))] p-2 shadow-[0_18px_45px_rgba(var(--shadow),0.18)]">
        <button type="button" className={buttonClassName} onClick={() => handleSelect(onDownloadPdf)}>
          <span>Descargar PDF</span>
        </button>
        <button type="button" className={buttonClassName} onClick={() => handleSelect(onViewHistory)}>
          <span>Ver historial</span>
        </button>
        {canRevalorizar ? (
          <button type="button" className={buttonClassName} onClick={() => handleSelect(onOpenRevalorizar)}>
            <span>Revalorizar</span>
          </button>
        ) : null}
        {actions.map((action) => (
          <button
            key={action.key}
            type="button"
            disabled={!action.enabled}
            title={action.reason}
            className={buttonClassName}
            onClick={() => handleSelect(() => onAction(action), action.enabled)}
          >
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    </details>
  );
}

function GuiaHeaderOperativo({ guiaNumero, operationalState, routeLabel, emissionDate, compact, viaLabel, primaryAction, onPrimaryAction, onBack, onRefresh, onPreviewPdf, onPrintPdf, onDownloadPdf, onViewHistory, extraActions, onExtraAction, canRevalorizar, onOpenRevalorizar, transitioningKey }) {
  return (
    <div className="sticky top-0 z-20 -mx-4 bg-[rgb(var(--bg))] px-4 pb-2 pt-2 md:-mx-5 md:px-5" data-qa-topbar>
      <Surface className="bg-[rgb(var(--panel))]">
        <div className={`flex flex-wrap items-center justify-between gap-2 ${compact ? 'px-3 py-1.5 md:px-4' : 'px-4 py-2 md:px-5'}`}>
          <div className="flex min-w-0 items-center gap-2.5">
            <Button type="button" variant="ghost" size="icon" onClick={onBack} className={`${compact ? 'h-8 w-8' : 'h-9 w-9'} rounded-xl`} aria-label="Volver">
              <ArrowLeft size={16} aria-hidden="true" />
            </Button>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                <h1 className={`truncate font-semibold tracking-tight text-[rgb(var(--fg))] ${compact ? 'text-[0.95rem] md:text-[1.1rem]' : 'text-[1.05rem] md:text-[1.2rem]'}`}>{guiaNumero}</h1>
                <StatusBadge label={operationalState} />
                <span className={`truncate text-[rgba(var(--fg),0.68)] ${compact ? 'text-[10.5px]' : 'text-[11px]'}`}>{routeLabel}</span>
                <span className={`truncate text-[rgba(var(--fg),0.62)] ${compact ? 'text-[10.5px]' : 'text-[11px]'}`}>Emitida {emissionDate}</span>
                {viaLabel ? <span className={`truncate text-[rgba(var(--fg),0.56)] ${compact ? 'text-[10px]' : 'text-[11px]'}`}>{viaLabel}</span> : null}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <QuickActionButton label="Refrescar" onClick={onRefresh} icon={RefreshCw} compact={compact} />
            <QuickActionButton label="Ver PDF" onClick={onPreviewPdf} icon={FileText} compact={compact} />
            <QuickActionButton label="Imprimir" onClick={onPrintPdf} icon={Receipt} compact={compact} />
            <MoreActionsMenu compact={compact} actions={extraActions} onAction={onExtraAction} onDownloadPdf={onDownloadPdf} onViewHistory={onViewHistory} canRevalorizar={canRevalorizar} onOpenRevalorizar={onOpenRevalorizar} />
            {primaryAction ? (
              <Button type="button" onClick={() => onPrimaryAction(primaryAction)} disabled={!primaryAction.enabled || transitioningKey === primaryAction.label} className={`${compact ? 'h-8 px-3 text-xs' : 'h-9 px-4'} rounded-xl`} aria-label={primaryAction.label} title={primaryAction.reason}>
                {transitioningKey === primaryAction.label ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                {primaryAction.label}
              </Button>
            ) : null}
          </div>
        </div>
      </Surface>
    </div>
  );
}

function SectionTitle({ children, aside = null }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.58)]">{children}</h2>
      {aside ? <div className="text-[11px] text-[rgba(var(--fg),0.54)]">{aside}</div> : null}
    </div>
  );
}

function GuiaKpiStrip({ guia }) {
  const items = [
    { label: 'Bultos', value: formatMetricValue(guia?.bultos, (value) => formatNumber(value, 0)) },
    { label: 'Kg reales', value: formatMetricValue(guia?.kgReales ?? guia?.kg_reales, (value) => formatNumber(value, 2)) },
    { label: 'Kg aforados', value: formatMetricValue(guia?.aforo ?? guia?.kg_aforado, (value) => formatNumber(value, 2)) },
    { label: 'M3', value: formatMetricValue(guia?.m3, (value) => formatNumber(value, 3)) },
    { label: 'Valor declarado', value: formatMoney(guia?.valorDeclarado ?? guia?.valor_declarado ?? 0) },
  ];

  return (
    <div className="grid grid-cols-2 overflow-hidden rounded-xl border border-[rgba(var(--border),0.55)] sm:grid-cols-3 xl:grid-cols-5">
      {items.map((item) => (
        <div key={item.label} className="min-w-0 border-b border-r border-[rgba(var(--border),0.4)] bg-[rgba(var(--subpanel),0.55)] px-3 py-2">
          <div className="truncate text-[10px] uppercase tracking-[0.12em] text-[rgba(var(--fg),0.5)]" title={item.label}>{item.label}</div>
          <div className="mt-0.5 truncate text-[15px] font-semibold tabular-nums text-[rgb(var(--fg))]" title={item.value}>{item.value}</div>
        </div>
      ))}
    </div>
  );
}

function PartyCard({ role, name, helper = null, lines = [], accent = false, payerLabel = null }) {
  const visibleLines = lines.filter((line) => hasDisplayValue(line?.value) && line.value !== 'No asignado');
  return (
    <article className={`min-w-0 rounded-xl border px-3 py-2.5 ${accent ? 'border-[rgba(var(--brand),0.35)] bg-[rgba(var(--brand),0.08)]' : 'border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.55)]'}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--fg),0.5)]">{role}</span>
        {payerLabel ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[rgba(var(--brand),0.4)] bg-[rgba(var(--brand),0.12)] px-2 py-0.5 text-[10px] font-semibold text-[rgb(var(--fg))]">
            <Wallet size={11} aria-hidden="true" />Paga · {payerLabel}
          </span>
        ) : helper ? <span className="truncate text-[10px] text-[rgba(var(--fg),0.5)]">{helper}</span> : null}
      </div>
      <div className="mt-1 truncate text-sm font-semibold text-[rgb(var(--fg))]" title={name}>{name}</div>
      {visibleLines.length ? (
        <dl className="mt-1.5 space-y-0.5 text-[12px] text-[rgba(var(--fg),0.7)]">
          {visibleLines.map((line) => (
            <div key={line.label} className="flex min-w-0 gap-1.5">
              <dt className="sr-only">{line.label}</dt>
              <dd className="line-clamp-2 break-words" title={line.value}>{line.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <div className="mt-1.5 text-[12px] text-[rgba(var(--fg),0.46)]">Sin datos de contacto</div>
      )}
    </article>
  );
}

function GuiaPartes({ clientName, paymentConditionLabel, remitente, destinatario }) {
  // The payer is usually one of the parties: mark that card instead of repeating it.
  const payerName = clientName?.primary;
  const remitentePaga = isSameParty(payerName, remitente.display.primary);
  const destinatarioPaga = !remitentePaga && isSameParty(payerName, destinatario.display.primary);
  const terceroPaga = !remitentePaga && !destinatarioPaga && hasMeaningfulText(payerName) && payerName !== 'Sin dato registrado';

  return (
    <section>
      <SectionTitle>Partes de la guía</SectionTitle>
      <div className={`grid gap-2 ${terceroPaga ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        <PartyCard
          role="Remitente"
          name={remitente.display.primary}
          helper={remitente.display.secondary}
          lines={[
            { label: 'Domicilio', value: joinParts(remitente.domicilio, remitente.localidad) },
            { label: 'Teléfono', value: remitente.telefono },
          ]}
          accent={remitentePaga}
          payerLabel={remitentePaga ? paymentConditionLabel : null}
        />
        <PartyCard
          role="Destinatario"
          name={destinatario.display.primary}
          helper={destinatario.display.secondary}
          lines={[
            { label: 'Domicilio', value: joinParts(destinatario.domicilio, destinatario.localidad) },
            { label: 'Teléfono', value: destinatario.telefono },
          ]}
          accent={destinatarioPaga}
          payerLabel={destinatarioPaga ? paymentConditionLabel : null}
        />
        {terceroPaga ? (
          <PartyCard
            role="Paga (tercero)"
            name={payerName}
            helper={clientName?.secondary}
            lines={[{ label: 'Condición', value: paymentConditionLabel }]}
            accent
          />
        ) : null}
      </div>
    </section>
  );
}

function TransportItem({ label, value, helper = null }) {
  return (
    <div className="min-w-[140px] flex-1 px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.14em] text-[rgba(var(--fg),0.5)]">{label}</div>
      <div className="mt-0.5 break-words text-[13px] font-semibold tabular-nums text-[rgb(var(--fg))]">{value}</div>
      {helper ? <div className="text-[11px] text-[rgba(var(--fg),0.56)]">{helper}</div> : null}
    </div>
  );
}

function GuiaTransporte({ guia }) {
  const transport = getTransportDisplay(guia);
  const fields = [
    { label: 'Línea', value: transport.linea, helper: transport.origen && transport.destino ? `${transport.origen} → ${transport.destino}` : null },
    { label: 'Servicio', value: guia?.tipoEntrega || guia?.tipo_entrega },
    { label: 'Foja', value: transport.foja },
    { label: 'Reparto', value: guia?.reparto_id ? `#${guia.reparto_id}` : '' },
    { label: 'Chofer', value: transport.chofer },
    { label: 'Unidad', value: transport.unidad },
  ];
  const { assigned, pending } = splitTransportFields(fields);
  const extras = [
    { label: 'Bolsa documental', value: guia?.bolsa_documental?.identificador ? joinParts(guia.bolsa_documental.identificador, guia.bolsa_documental.estado) : '' },
    { label: 'Origen canónico', value: joinParts(guia?.tipo_origen, guia?.guia_origen_numero ? `Guía ${guia.guia_origen_numero}` : null) },
    { label: 'Sucursal transcripción', value: guia?.sucursal_origen_transcripcion_nombre || '' },
    { label: 'Vía', value: guia?.sucursalVia?.nombre || '' },
  ].filter((item) => hasDisplayValue(item.value));

  return (
    <section>
      <SectionTitle>Transporte</SectionTitle>
      <div className="overflow-hidden rounded-xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.55)]">
        {assigned.length || extras.length ? (
          <div className="flex flex-wrap [&>*]:border-b [&>*]:border-r [&>*]:border-[rgba(var(--border),0.4)]">
            {[...assigned, ...extras].map((item) => <TransportItem key={item.label} label={item.label} value={item.value} helper={item.helper} />)}
          </div>
        ) : null}
        {pending.length ? (
          <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 text-[12px] text-[rgba(var(--fg),0.62)]">
            <Clock3 size={13} aria-hidden="true" />
            <span>Falta asignar:</span>
            {pending.map((label) => (
              <span key={label} className="rounded-full border border-dashed border-[rgba(var(--border),0.9)] px-2 py-0.5 text-[11px] text-[rgba(var(--fg),0.7)]">{label}</span>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function GuiaTimelineCompacto({ guia, operationalState, timelinePreview, onViewHistory }) {
  const currentIndex = TIMELINE_STEPS.findIndex((step, index) => step.states.includes(operationalState) && (TIMELINE_STEPS[index + 1] ? !TIMELINE_STEPS[index + 1].states.includes(operationalState) : true));
  const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
  // Emission is a persisted fact of the guide even when no trace event was recorded for it.
  const stepDates = {
    emitida: guia?.fechaEmision || guia?.fecha_emision || guia?.created_at || null,
    ...(timelinePreview?.stepMoments || {}),
  };

  return (
    <section>
      <SectionTitle aside={<Button type="button" variant="ghost" size="sm" onClick={onViewHistory} className="h-6 rounded-lg px-2 text-[11px]">Ver historial</Button>}>Recorrido</SectionTitle>
      <ol className="flex items-start overflow-x-auto pb-1">
        {TIMELINE_STEPS.map((step, index) => {
          const completed = step.states.includes(operationalState);
          const isCurrent = index === safeCurrentIndex;
          const Icon = TIMELINE_STEP_ICONS[step.id] || Clock3;
          const stepDate = stepDates[step.id];
          const dotClass = isCurrent
            ? 'border-[rgb(var(--brand))] bg-[rgb(var(--brand))] text-[rgb(var(--bg))] shadow-[0_0_0_4px_rgba(var(--brand),0.18)]'
            : completed
              ? 'border-[rgba(var(--success),0.95)] bg-[rgba(var(--success),0.95)] text-[rgb(var(--bg))]'
              : 'border-[rgba(var(--border),0.9)] bg-[rgb(var(--panel))] text-[rgba(var(--fg),0.42)]';
          const caption = stepDate ? formatDate(stepDate, { dateOnly: true }) : isCurrent ? 'Actual' : completed ? 'Cumplido' : '';
          return (
            <li key={step.id} className="relative flex min-w-[88px] flex-1 flex-col items-center text-center" title={stepDate ? `${step.label} · ${formatDate(stepDate)}` : step.label}>
              {index > 0 ? <span className={`absolute right-1/2 top-[13px] h-0.5 w-full ${index <= safeCurrentIndex ? 'bg-[rgba(var(--success),0.7)]' : 'bg-[rgba(var(--border),0.8)]'}`} aria-hidden="true" /> : null}
              <span className={`relative z-[1] flex h-7 w-7 items-center justify-center rounded-full border-2 ${dotClass}`}>
                {completed && !isCurrent ? <CheckCircle2 size={13} aria-hidden="true" /> : <Icon size={13} aria-hidden="true" />}
              </span>
              <span className={`mt-1 text-[12px] ${isCurrent ? 'font-semibold text-[rgb(var(--fg))]' : completed ? 'text-[rgba(var(--fg),0.78)]' : 'text-[rgba(var(--fg),0.48)]'}`}>{step.shortLabel}</span>
              <span className={`text-[10px] ${isCurrent ? 'text-[rgb(var(--brand))]' : 'text-[rgba(var(--fg),0.5)]'}`}>{caption || ' '}</span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

const sumBy = (rows, pick) => rows.reduce((total, row) => total + (Number(pick(row)) || 0), 0);

function GuiaRemitosDestacados({ remitos, compact = false }) {
  const cellClass = compact ? 'px-2 py-1.5' : 'px-3 py-2';

  return (
    <Surface>
      <div className={compact ? 'px-3 py-2.5' : 'px-4 py-3'} data-qa-results-header>
        <SectionTitle aside={<Badge variant="outline" className="rounded-full">{remitos.length}</Badge>}>Remitos</SectionTitle>
        {remitos.length ? (
          <div className="overflow-x-auto rounded-xl border border-[rgba(var(--border),0.55)]">
            <table className={`min-w-full border-collapse ${compact ? 'text-[12px]' : 'text-sm'}`}>
              <thead className="bg-[rgba(var(--subpanel),0.92)] text-left text-[10px] uppercase tracking-[0.14em] text-[rgba(var(--fg),0.56)]">
                <tr>
                  <th className={cellClass}>Remito</th>
                  <th className={cellClass}>Contenido</th>
                  <th className={`${cellClass} text-right`}>Bultos</th>
                  <th className={`${cellClass} text-right`}>Kg</th>
                  <th className={`${cellClass} text-right`}>M3</th>
                  <th className={`${cellClass} text-right`}>Valor decl.</th>
                </tr>
              </thead>
              <tbody>
                {remitos.map((row, index) => {
                  const reference = joinParts(row.origen ? `Orig. ${row.origen}` : null, row.referencia_externa ? `Ref. ${row.referencia_externa}` : null);
                  return (
                    <tr key={row.id || `${row.numero || 'remito'}-${index + 1}`} data-qa-row className="border-t border-[rgba(var(--border),0.34)] bg-[rgba(var(--panel),0.82)] align-top tabular-nums">
                      <td className={`${cellClass} font-semibold text-[rgb(var(--fg))]`}>{row.numero || row.remito || `Remito ${index + 1}`}</td>
                      <td className={`${cellClass} text-[rgba(var(--fg),0.78)]`}>
                        <div>{row.descripcion || '—'}</div>
                        {reference ? <div className="text-[11px] text-[rgba(var(--fg),0.56)]">{reference}</div> : null}
                      </td>
                      <td className={`${cellClass} text-right`}>{formatNumber(row.bultos, 0)}</td>
                      <td className={`${cellClass} text-right`}>{formatNumber(row.kgReales ?? row.kg_reales, 2)}</td>
                      <td className={`${cellClass} text-right`}>{Number(row.m3 || 0) > 0 ? formatNumber(row.m3, 3) : '—'}</td>
                      <td className={`${cellClass} text-right`}>{formatMoney(row.valorDeclarado ?? row.valor_declarado)}</td>
                    </tr>
                  );
                })}
              </tbody>
              {remitos.length > 1 ? (
                <tfoot>
                  <tr className="border-t border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.7)] font-semibold tabular-nums text-[rgb(var(--fg))]">
                    <td className={cellClass} colSpan={2}>Total</td>
                    <td className={`${cellClass} text-right`}>{formatNumber(sumBy(remitos, (row) => row.bultos), 0)}</td>
                    <td className={`${cellClass} text-right`}>{formatNumber(sumBy(remitos, (row) => row.kgReales ?? row.kg_reales), 2)}</td>
                    <td className={`${cellClass} text-right`}>{formatNumber(sumBy(remitos, (row) => row.m3), 3)}</td>
                    <td className={`${cellClass} text-right`}>{formatMoney(sumBy(remitos, (row) => row.valorDeclarado ?? row.valor_declarado))}</td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        ) : (
          <div className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.66)] px-3 py-2 text-sm text-[rgba(var(--fg),0.64)]">
            La guía no tiene remitos asociados visibles.
          </div>
        )}
      </div>
    </Surface>
  );
}

function GuiaAlertas({ alerts, compact = false }) {
  if (!alerts.length) return null;
  return (
    <div className="grid gap-1.5">
      {alerts.map((alert) => (
        <div key={alert.id} className={`rounded-xl border ${compact ? 'px-2 py-1.5' : 'px-2.5 py-2'} ${getToneClasses(alert.tone)}`}>
          <div className="flex items-center gap-1.5 text-[12px] font-semibold"><AlertTriangle size={12} aria-hidden="true" /><span>{alert.label}</span></div>
          <div className="mt-0.5 text-[11px] text-[rgba(var(--fg),0.74)]">{alert.detail}</div>
        </div>
      ))}
    </div>
  );
}

function GuiaPanelClave({ guia, detail, nextAction, primaryAction = null, onPrimaryAction, transitioningKey = '', billingStatus, collectionStatus, timelinePreview, operationalState, alerts, compact = false, stickyTopClass = 'top-24' }) {
  const latestEvent = timelinePreview?.latestEvent || null;
  const emissionDate = guia?.created_at || guia?.fechaEmision || guia?.fecha_emision || null;
  const latestEventDate = latestEvent?.created_at || emissionDate;
  const latestEventLabel = latestEvent ? ((TIMELINE_EVENT_META[latestEvent.tipo] || {}).label || String(latestEvent.tipo || 'Evento').replaceAll('_', ' ')) : 'Guía emitida';
  const latestEventUser = pickFirstMeaningful(latestEvent?.usuario, latestEvent?.user, latestEvent?.created_by, latestEvent?.usuario_nombre, latestEvent ? null : guia?.usuario);
  const latestEventDetail = pickFirstMeaningful(latestEvent?.descripcion, latestEvent?.detalle);
  const stateStartedAt = getCurrentStateStartedAt({ guia, operationalState, timelinePreview });
  const slaModel = getOperationalSlaModel(operationalState, stateStartedAt);
  const totalValorizado = guia?.valorizacion_snapshot?.header?.totalFinal || guia?.guia_valorizacion?.header?.totalFinal || guia?.total_valorizado || guia?.total;
  const padding = compact ? 'px-3 py-2.5' : 'px-4 py-3';

  return (
    <Surface className={`xl:sticky ${stickyTopClass} overflow-hidden`}>
      <div className={`${padding} border-b border-[rgba(var(--brand),0.25)] bg-[linear-gradient(180deg,rgba(var(--brand),0.14),rgba(var(--brand),0.04))]`}>
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[rgba(var(--fg),0.58)]">Próximo paso</div>
        <div className="mt-1 text-base font-semibold text-[rgb(var(--fg))]">{nextAction.label}</div>
        <p className="mt-0.5 text-[12px] leading-5 text-[rgba(var(--fg),0.7)]">{nextAction.description}</p>
        {primaryAction ? (
          <Button type="button" onClick={() => onPrimaryAction(primaryAction)} disabled={!primaryAction.enabled || transitioningKey === primaryAction.label} title={primaryAction.reason} className="mt-2.5 h-9 w-full rounded-xl font-semibold">
            {transitioningKey === primaryAction.label ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {primaryAction.label}
          </Button>
        ) : null}
      </div>

      <div className={`${padding} space-y-3`}>
        <div className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2 ${getToneClasses(slaModel.tone)}`}>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-80">SLA del estado</div>
            <div className="text-sm font-semibold">{slaModel.label}</div>
          </div>
          <div className="text-right text-[11px] leading-4 text-[rgba(var(--fg),0.72)]">
            <div><strong className="text-[rgb(var(--fg))]">{slaModel.elapsedLabel}</strong> en {String(operationalState || 'SIN_DATO').replaceAll('_', ' ').toLowerCase()}</div>
            <div>Objetivo {slaModel.targetLabel}</div>
          </div>
        </div>

        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--fg),0.5)]">Último movimiento</div>
          <div className="mt-1 flex items-start gap-2">
            <Clock3 size={14} className="mt-0.5 shrink-0 text-[rgba(var(--fg),0.5)]" aria-hidden="true" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[rgb(var(--fg))]">{latestEventLabel}</div>
              <div className="text-[11px] text-[rgba(var(--fg),0.6)]">{joinParts(latestEventDate ? formatCompactDateTime(latestEventDate) : null, latestEventUser)}</div>
              {latestEventDetail ? <div className="mt-0.5 text-[12px] text-[rgba(var(--fg),0.7)]">{latestEventDetail}</div> : null}
            </div>
          </div>
        </div>

        <div className="border-t border-[rgba(var(--border),0.4)] pt-1">
          <CompactRow label="Sucursal actual" value={formatOptionalValue(detail.sucursalActual?.nombre || guia?.sucursalActual?.nombre)} compact dense />
          <CompactRow label="Total valorizado" value={formatMoney(totalValorizado)} strong compact dense />
          <CompactRow label="Facturación" value={billingStatus.label} compact dense />
          <CompactRow label="Cobranza" value={collectionStatus.label} compact dense />
        </div>

        {alerts.length ? (
          <div>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[rgba(var(--fg),0.5)]">Alertas</div>
            <GuiaAlertas alerts={alerts.slice(0, 3)} compact={compact} />
            {alerts.length > 3 ? <div className="mt-1 text-[11px] text-[rgba(var(--fg),0.58)]">Hay {alerts.length - 3} alertas adicionales en el historial.</div> : null}
          </div>
        ) : null}
      </div>
    </Surface>
  );
}

function GuiaPrimerPliegue({ guia, operationalState, timelinePreview, onViewHistory, clientName, paymentConditionLabel, remitente, destinatario, compact = false }) {
  return (
    <Surface>
      <div className={`space-y-4 ${compact ? 'px-3 py-3' : 'px-4 py-4'}`}>
        <GuiaTimelineCompacto guia={guia} operationalState={operationalState} timelinePreview={timelinePreview} onViewHistory={onViewHistory} />
        <GuiaKpiStrip guia={guia} />
        <GuiaPartes clientName={clientName} paymentConditionLabel={paymentConditionLabel} remitente={remitente} destinatario={destinatario} />
        <GuiaTransporte guia={guia} />
      </div>
    </Surface>
  );
}

function TimelineTab({ guia, guiaId, sucursalId }) {
  const [{ data, fetching, error }] = useQuery({
    query: GUIA_TIMELINE,
    variables: { guia_id: guiaId },
    pause: !guiaId || !sucursalId,
    requestPolicy: 'network-only',
  });

  const items = useMemo(() => {
    const rows = Array.isArray(data?.guiaTimeline) ? data.guiaTimeline : [];
    const sorted = [...rows].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return withEmissionEvent(sorted, guia);
  }, [data, guia]);

  if (fetching) return <Surface className="p-5 text-sm text-[rgba(var(--fg),0.7)]">Cargando historial operativo...</Surface>;
  if (error) return <Surface className="p-5 text-sm text-[rgb(var(--danger))]">No se pudo cargar la trazabilidad: {error.message}</Surface>;
  if (!items.length) return <Surface className="p-5 text-sm text-[rgba(var(--fg),0.7)]">Todavía no hay eventos de trazabilidad para esta guía.</Surface>;

  return (
    <ol className="relative">
      {items.map((item, index) => {
        const meta = TIMELINE_EVENT_META[item.tipo] || { label: String(item.tipo || 'Evento').replaceAll('_', ' '), icon: Clock3 };
        const Icon = meta.icon;
        const isLatest = index === 0;
        return (
          <li key={item.id} className="relative flex gap-3 pb-4 last:pb-0">
            {index < items.length - 1 ? <span className="absolute left-[15px] top-8 h-[calc(100%-1.5rem)] w-0.5 bg-[rgba(var(--border),0.8)]" aria-hidden="true" /> : null}
            <span className={`relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${isLatest ? 'border-[rgb(var(--brand))] bg-[rgba(var(--brand),0.14)] text-[rgb(var(--fg))]' : 'border-[rgba(var(--border),0.9)] bg-[rgb(var(--panel))] text-[rgba(var(--fg),0.6)]'}`}>
              <Icon size={14} aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1 pt-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <p className="text-sm font-semibold text-[rgb(var(--fg))]">{meta.label}</p>
                <time className="text-[12px] tabular-nums text-[rgba(var(--fg),0.6)]" dateTime={String(item.created_at || '')}>{formatDate(item.created_at)}</time>
              </div>
              {item.descripcion ? <p className="mt-0.5 text-[13px] text-[rgba(var(--fg),0.72)]">{item.descripcion}</p> : null}
              <p className="mt-0.5 text-[11px] text-[rgba(var(--fg),0.5)]">
                {item.synthetic ? 'Tomado de la fecha de emisión' : joinParts(item.usuario_id ? `Usuario #${item.usuario_id}` : 'Sistema', item.sucursal_id ? `Sucursal #${item.sucursal_id}` : null)}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function useTimelinePreview({ guiaId, sucursalId }) {
  const [{ data }] = useQuery({
    query: GUIA_TIMELINE,
    variables: { guia_id: guiaId },
    pause: !guiaId || !sucursalId,
    requestPolicy: 'network-only',
  });

  return useMemo(() => {
    const items = Array.isArray(data?.guiaTimeline) ? data.guiaTimeline : [];
    const sorted = [...items].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    const stepMoments = sorted.reduce((acc, item) => {
      const stepId = resolveTimelineStepFromEvent(item);
      if (!stepId || acc[stepId]) return acc;
      acc[stepId] = item.created_at;
      return acc;
    }, {});
    return {
      latestEvent: sorted[0] || null,
      stepMoments,
    };
  }, [data]);
}

function GuiaTabsDetalle({ activeTab, setActiveTab, guia, detail, commercialStatus, collectionStatus, billingStatus, currentTariffSnapshot, valuationRows, valuationSnapshot, facturaRelacion, goToCobranza, goToFacturacion, navigate, operationalState, timelineSucursalId, revaluationHistory, canRevalorizar, revaluationReason, onOpenRevalorizar, compact = false, stickyTopClass = 'top-24' }) {
  return (
    <Surface>
      <div className={`sticky z-10 ${stickyTopClass} border-b border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.95)] ${compact ? 'px-2.5 py-1.5' : 'px-3 py-2.5'}`}>
        <div className="flex flex-wrap gap-1.5">
          {TABS.map((tab) => (
            <TabButton key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)} compact={compact}>
              {tab.label}
            </TabButton>
          ))}
        </div>
      </div>
      <div className={compact ? 'p-3' : 'p-4'}>

        {activeTab === 'economico' ? (
          <div className="grid gap-3 xl:grid-cols-[1.15fr_0.85fr]">
            <div className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.48)] p-3"><div className="grid gap-2 md:grid-cols-2"><MiniStat label="Condición de pago" value={commercialStatus.label} helper={commercialStatus.reason} icon={Wallet} /><MiniStat label="Cobranza" value={collectionStatus.label} helper={collectionStatus.reason} icon={Banknote} /><MiniStat label="Facturación" value={billingStatus.label} helper={billingStatus.reason} icon={Receipt} /><MiniStat label="Total valorizado" value={formatMoney(guia?.valorizacion_snapshot?.header?.totalFinal || guia?.guia_valorizacion?.header?.totalFinal || guia?.total_valorizado || guia?.total)} helper={formatSnapshotSource(guia?.valorizacion_snapshot?.source || guia?.guia_valorizacion?.source || currentTariffSnapshot?.source)} icon={Receipt} /></div><div className="mt-3 overflow-hidden rounded-2xl border border-[rgba(var(--border),0.6)]"><table className="min-w-full border-collapse text-sm"><thead className="bg-[rgba(var(--subpanel),0.92)] text-left text-[11px] uppercase tracking-[0.14em] text-[rgba(var(--fg),0.56)]"><tr><th className="px-3 py-2">Concepto</th><th className="px-3 py-2">Base</th><th className="px-3 py-2">Cálculo</th><th className="px-3 py-2 text-right">Importe</th></tr></thead><tbody>{valuationRows.map((row) => <tr key={row.id} className="border-t border-[rgba(var(--border),0.4)] bg-[rgba(var(--panel),0.72)]"><td className="px-3 py-2 font-medium text-[rgb(var(--fg))]">{row.concepto}</td><td className="px-3 py-2 text-[rgba(var(--fg),0.74)]">{row.base}</td><td className="px-3 py-2 text-[rgba(var(--fg),0.74)]">{row.calculo}</td><td className="px-3 py-2 text-right font-semibold text-[rgb(var(--fg))]">{formatMoney(row.importe)}</td></tr>)}</tbody></table></div></div>
            <div className="space-y-3 rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.48)] p-3"><div><div className="text-sm font-semibold text-[rgb(var(--fg))]">Acciones económicas</div><div className="mt-2 flex flex-wrap gap-2"><Button onClick={goToCobranza} disabled={operationalState !== 'ENTREGADA'} className="rounded-xl">Abrir cobranza</Button><Button onClick={goToFacturacion} disabled={Number(guia?.total_valorizado || 0) <= 0 || Boolean(guia?.facturada)} variant="outline" className="rounded-xl">Abrir facturación</Button>{canRevalorizar ? <Button onClick={onOpenRevalorizar} variant="outline" className="rounded-xl">Revalorizar</Button> : null}{facturaRelacion?.factura_id ? <Button variant="ghost" onClick={() => navigate(`/facturacion/${facturaRelacion.factura_id}`)} className="rounded-xl">Ver factura</Button> : null}</div>{revaluationReason ? <div className="mt-2 rounded-xl border border-[rgba(var(--warning),0.3)] bg-[rgba(var(--warning),0.12)] px-3 py-2 text-sm text-[rgb(var(--warning))]">{revaluationReason}</div> : null}</div><div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.82)] p-3"><div className="text-sm font-semibold text-[rgb(var(--fg))]">Historial de valorización</div>{revaluationHistory.length ? <div className="mt-2 space-y-2">{revaluationHistory.map((item) => <div key={item.id} className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.52)] p-3"><div className="flex flex-wrap items-center justify-between gap-2"><div className="text-sm font-semibold text-[rgb(var(--fg))]">{item.source}</div><div className="text-xs text-[rgba(var(--fg),0.6)]">{formatDate(item.fecha)}</div></div><div className="mt-2 grid gap-2 md:grid-cols-2"><CompactRow label="Usuario" value={item.usuario} /><CompactRow label="Motivo" value={item.motivo} /><CompactRow label="Total anterior" value={formatMoney(item.previousTotal)} /><CompactRow label="Total nuevo" value={formatMoney(item.newTotal)} /></div></div>)}</div> : <div className="mt-2 text-sm text-[rgba(var(--fg),0.62)]">No hay historial de revalorizaciones publicado.</div>}</div></div>
          </div>
        ) : null}

        {activeTab === 'historial' ? <TimelineTab guia={guia} guiaId={Number(guia?.id || 0)} sucursalId={timelineSucursalId} /> : null}

        {activeTab === 'observaciones' ? (
          <div className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.48)] p-3"><div className="whitespace-pre-wrap text-sm text-[rgba(var(--fg),0.72)]">{guia?.observaciones || 'Sin observaciones cargadas.'}</div></div>
        ) : null}

        {activeTab === 'documentacion' ? (
          <div className="grid gap-3 xl:grid-cols-2">
            <div className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.48)] p-3"><div className="mb-2 text-sm font-semibold text-[rgb(var(--fg))]">Territorialidad persistida</div><pre className="overflow-x-auto whitespace-pre-wrap text-xs text-[rgba(var(--fg),0.76)]">{JSON.stringify({ sucursal_origen: guia?.sucursalOrigen || null, sucursal_destino: guia?.sucursalDestino || null, sucursal_actual: guia?.sucursalActual || detail.sucursalActual || null, sucursal_via: guia?.sucursalVia || null, routing_mode: guia?.routing_mode || null, bolsa_documental: guia?.bolsa_documental || null, origen_canonico: { tipo_origen: guia?.tipo_origen || null, guia_origen_id: guia?.guia_origen_id || null, guia_origen_numero: guia?.guia_origen_numero || null, sucursal_origen_transcripcion_id: guia?.sucursal_origen_transcripcion_id || null, sucursal_origen_transcripcion_nombre: guia?.sucursal_origen_transcripcion_nombre || null } }, null, 2)}</pre></div>
            <div className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgba(var(--subpanel),0.48)] p-3"><div className="mb-2 text-sm font-semibold text-[rgb(var(--fg))]">Snapshots</div><pre className="overflow-x-auto whitespace-pre-wrap text-xs text-[rgba(var(--fg),0.76)]">{JSON.stringify({ remitente_snapshot: parseJsonSafe(guia?.remitente_snapshot) || null, destinatario_snapshot: parseJsonSafe(guia?.destinatario_snapshot) || null, detalle_valorizacion: valuationSnapshot || null }, null, 2)}</pre></div>
          </div>
        ) : null}
      </div>
    </Surface>
  );
}
export default function GuiaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant, token, user, sucursal } = useAuth();
  const { isOperatorCompact } = useOperatorCompactViewport();
  const roleMode = useMemo(() => deriveRoleMode(user), [user]);
  const [activeTab, setActiveTab] = useState('historial');
  const [loading, setLoading] = useState(true);
  const [transitioningKey, setTransitioningKey] = useState('');
  const [error, setError] = useState(null);
  const [facturaRelacion, setFacturaRelacion] = useState(null);
  const [snapshotBundle, setSnapshotBundle] = useState(null);
  const [revalorizacionOpen, setRevalorizacionOpen] = useState(false);
  const [detail, setDetail] = useState({ guia: null, remitos: [], historial: [], sucursalActual: null });
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));

  const [, cambiarEstadoGuia] = useMutation(CAMBIAR_ESTADO_GUIA);
  const guiaNumericId = Number(detail.guia?.id || id || 0) || 0;
  const [retornablesResult] = useQuery({
    query: RETORNABLES_MOVIMIENTOS_QUERY,
    variables: { filtro: { guiaId: guiaNumericId || undefined } },
    pause: !guiaNumericId || !hasPermission(user, 'RETORNABLES_VER'),
  });
  const guiaRetornables = retornablesResult.data?.retornablesMovimientos || [];

  const resolveGuideIdByNumero = useCallback(async (numeroGuia) => {
    if (!numeroGuia) return null;
    const payload = await fetchGuides({ filters: { nro_guia: String(numeroGuia) }, page: 1, limit: 10 });
    const rows = Array.isArray(payload?.data) ? payload.data : [];
    const match = rows.find((item) => String(item?.nro_guia || '').trim() === String(numeroGuia).trim()) || rows[0];
    return match?.id ? String(match.id) : null;
  }, []);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [payload, snapshots] = await Promise.all([
        fetchGuideDetail(id),
        fetchGuideTariffSnapshots({ tenant, token, guiaId: id }).catch(() => null),
      ]);
      setDetail({ guia: payload.guia || null, remitos: payload.remitos || [], historial: payload.historial || [], sucursalActual: payload.sucursalActual || null });
      setSnapshotBundle(snapshots);
    } catch (err) {
      const recoveryNumeroGuia = location.state?.guiaNumero || null;
      if (recoveryNumeroGuia) {
        try {
          const resolvedGuideId = await resolveGuideIdByNumero(recoveryNumeroGuia);
          if (resolvedGuideId && String(resolvedGuideId) !== String(id)) {
            navigate(`/guias/${resolvedGuideId}`, { replace: true, state: location.state });
            return;
          }
        } catch {
          // noop
        }
      }
      setError(err instanceof Error ? err.message : 'No se pudo cargar la guia.');
    } finally {
      setLoading(false);
    }
  }, [id, location.state, navigate, resolveGuideIdByNumero, tenant, token]);

  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => {
    if (location.state?.activeTab && TABS.some((tab) => tab.id === location.state.activeTab)) setActiveTab(location.state.activeTab);
  }, [location.state]);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const syncViewport = () => setViewportWidth(window.innerWidth);
    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);


  useEffect(() => {
    let active = true;
    if (activeTab !== 'economico' || !guiaNumericId) return undefined;
    fetchFacturaDeGuia({ tenant, token, guiaId: guiaNumericId }).then((payload) => { if (active) setFacturaRelacion(payload || null); }).catch(() => { if (active) setFacturaRelacion(null); });
    return () => { active = false; };
  }, [activeTab, guiaNumericId, tenant, token]);

  const guia = detail.guia;
  const operationalState = normalizeState(guia?.estado);
  const remitenteSnapshot = useMemo(() => parseJsonSafe(guia?.remitente_snapshot), [guia?.remitente_snapshot]);
  const destinatarioSnapshot = useMemo(() => parseJsonSafe(guia?.destinatario_snapshot), [guia?.destinatario_snapshot]);
  const persistedValuationSnapshot = guia?.valorizacion_snapshot || guia?.guia_valorizacion || null;
  const valuationSnapshot = persistedValuationSnapshot;
  const currentTariffSnapshot = snapshotBundle?.current || null;
  const valuationRows = useMemo(() => flattenPersistedValuationRows(persistedValuationSnapshot, guia), [persistedValuationSnapshot, guia]);
  const revaluationHistory = useMemo(() => buildRevaluationHistory(snapshotBundle?.headers || []), [snapshotBundle?.headers]);
  const timelinePreview = useTimelinePreview({ guiaId: guiaNumericId, sucursalId: Number(sucursal?.id || 0) });

  const guiaNumero = useMemo(() => {
    if (!guia) return 'No asignado';
    const numero = guia.numero ?? guia.numero_guia ?? guia.id ?? 'No asignado';
    const puntoVenta = guia.puntoVentaId ?? guia.punto_venta_id;
    return puntoVenta ? formatNumeroGuia(puntoVenta, numero) : numero;
  }, [guia]);

  const remitenteNombre = buildEntityDisplay(remitenteSnapshot || guia?.remitente, guia?.remitente_id ?? guia?.remitente?.id ?? null, 'Sin remitente');
  const destinatarioNombre = buildEntityDisplay(destinatarioSnapshot || guia?.destinatario, guia?.destinatario_id ?? guia?.destinatario?.id ?? null, 'Sin destinatario');
  const remitenteDomicilio = remitenteSnapshot?.direccion || guia?.remitente?.domicilio || 'No asignado';
  const destinatarioDomicilio = destinatarioSnapshot?.direccion || guia?.destinatario?.domicilio || 'No asignado';
  const remitenteLocalidad = remitenteSnapshot?.localidad || guia?.remitente?.localidad || 'No asignado';
  const destinatarioLocalidad = destinatarioSnapshot?.localidad || guia?.destinatario?.localidad || 'No asignado';
  const remitenteTelefono = remitenteSnapshot?.telefono || guia?.remitente?.telefono || 'No asignado';
  const destinatarioTelefono = destinatarioSnapshot?.telefono || guia?.destinatario?.telefono || 'No asignado';
  const routeLabel = inferRouteLabel(guia);
  const commercialStatus = useMemo(() => getCommercialStatus(guia), [guia]);
  const billingStatus = useMemo(() => getBillingStatus(guia), [guia]);
  const canRevalorizar = useMemo(() => hasPermission(user, 'GUIAS_REVALORIZAR') && snapshotBundle?.eligibility?.allowed, [snapshotBundle?.eligibility?.allowed, user]);
  const revaluationReason = useMemo(() => {
    if (!hasPermission(user, 'GUIAS_REVALORIZAR')) return 'Sin permiso para revalorizar.';
    if (!snapshotBundle?.eligibility?.allowed) return snapshotBundle?.eligibility?.message || 'La guia no puede revalorizarse.';
    return '';
  }, [snapshotBundle?.eligibility?.allowed, snapshotBundle?.eligibility?.message, user]);
  const collectionStatus = useMemo(() => getCollectionStatus(guia, operationalState), [guia, operationalState]);
  const nextAction = useMemo(() => getNextActionHint(guia, operationalState), [guia, operationalState]);
  const { primaryAction, secondaryActions } = useMemo(() => buildActionModel({ guia, state: operationalState, nextAction, collectionStatus }), [guia, operationalState, nextAction, collectionStatus]);
  const operationalAlerts = useMemo(() => buildOperationalAlerts({ guia, state: operationalState, billingStatus, collectionStatus, timelinePreview: timelinePreview?.latestEvent }), [guia, operationalState, billingStatus, collectionStatus, timelinePreview]);
  const menuActions = useMemo(() => secondaryActions.filter((action) => action.key !== 'refresh'), [secondaryActions]);

  const clientName = useMemo(() => getClientName(guia), [guia]);
  const payerRoleLabel = useMemo(() => {
    const condicionPago = String(guia?.condicion_pago || '').toUpperCase();
    if (condicionPago === 'ORIGEN') return 'Remitente';
    if (condicionPago === 'DESTINO') return 'Destinatario';
    return null;
  }, [guia?.condicion_pago]);
  const paymentConditionLabel = useMemo(() => formatPaymentCondition(guia?.formaPago || guia?.modalidadCobro) || payerRoleLabel || 'Sin condición registrada', [guia?.formaPago, guia?.modalidadCobro, payerRoleLabel]);
  const isCompactDesktop = viewportWidth >= 1366 && viewportWidth < 1600;
  const compactLayout = isOperatorCompact || isCompactDesktop;
  const stickyTopClass = compactLayout ? 'top-[82px]' : 'top-[92px]';

  useOperatorCompactQaScreen('Detalle de guia', [
    'Timeline horizontal compacto y barra operativa resumida',
    'Banda unica de actores y remitos densificados',
    'Panel lateral sticky y graficos relegados bajo el pliegue operativo',
  ]);

  const runTransition = useCallback(async (targetState, actionLabel, extra = {}) => {
    if (!guia?.id || !sucursal?.id) return;
    setTransitioningKey(actionLabel);
    try {
      const result = await cambiarEstadoGuia({
        input: {
          guia_id: Number(guia.id),
          sucursal_id: Number(sucursal.id),
          estado_nuevo: targetState,
          descripcion: extra.description || null,
          metadata_json: extra.metadata ? JSON.stringify(extra.metadata) : null,
          override_reason: extra.overrideReason || null,
        },
      });
      if (result.error) throw new Error(result.error.message || `No se pudo ejecutar ${actionLabel}.`);
      await Swal.fire({ title: 'Accion registrada', text: `${actionLabel} aplicada con trazabilidad.`, icon: 'success', timer: 1400, showConfirmButton: false });
      await loadDetail();
    } catch (err) {
      await Swal.fire('Error', err.message || `No se pudo ejecutar ${actionLabel}.`, 'error');
    } finally {
      setTransitioningKey('');
    }
  }, [cambiarEstadoGuia, guia?.id, loadDetail, sucursal?.id]);

  const handleAction = useCallback(async (action) => {
    if (!action?.enabled) return;
    if (action.key === 'refresh') { await loadDetail(); return; }
    if (action.key === 'foja') { navigate('/fojas/emision', { state: { guiaId: guia?.id, guiaNumero, linea: guia?.linea || guia?.linea_numero || '', sucursalDestinoId: guia?.sucursalDestino?.id || guia?.sucursal_destino_id || '', fromGuiaDetail: true } }); return; }
    if (action.key === 'reparto') { navigate('/repartos/nuevo', { state: { guiaId: guia?.id, guiaNumero, fromGuiaDetail: true, lineId: guia?.linea || guia?.linea_numero || '' } }); return; }
    if (action.key === 'etiquetas') { navigate(`/cargas/etiquetas?guiaId=${encodeURIComponent(guia?.id ?? '')}`); return; }
    if (action.key === 'incidencia') { navigate('/cargas/novedades', { state: { guiaId: guia?.id, guiaNumero, fromGuiaDetail: true } }); return; }
    if (action.key === 'cobranza') { navigate('/tesoreria/cobranzas/nueva', { state: { guiaId: guia?.id, guiaNumero, fromGuiaDetail: true } }); return; }
    if (action.key === 'confirmar') {
      const decision = await Swal.fire({ title: 'Confirmar entrega', text: 'Esta accion cerrara operativamente la guia como entregada.', icon: 'question', showCancelButton: true, confirmButtonText: 'Confirmar entrega', cancelButtonText: 'Cancelar' });
      if (decision.isConfirmed) await runTransition('ENTREGADA', action.label, { description: 'Entrega confirmada desde detalle operativo de guia.', metadata: { source: 'guia_detalle', action: action.key, roleMode } });
      return;
    }
    if (action.key === 'reabrir') {
      const decision = await Swal.fire({ title: 'Reabrir guia', input: 'textarea', inputLabel: 'Motivo operativo', inputPlaceholder: 'Incidencia resuelta, guia lista para reintento...', inputValidator: (value) => (!value ? 'Indica el motivo para reabrir.' : undefined), showCancelButton: true, confirmButtonText: 'Reabrir y volver a reparto', cancelButtonText: 'Cancelar' });
      if (decision.isConfirmed) await runTransition('EN_REPARTO', action.label, { description: `Reapertura desde detalle de guia. Motivo: ${decision.value}`, metadata: { source: 'guia_detalle', action: action.key, reason: decision.value, roleMode } });
      return;
    }
    if (action.key === 'anular') {
      const decision = await Swal.fire({ title: 'Anular guia', input: 'textarea', inputLabel: 'Motivo obligatorio', inputPlaceholder: 'Describi la causa de la anulacion...', inputValidator: (value) => (!value ? 'La anulacion requiere un motivo auditado.' : undefined), icon: 'warning', showCancelButton: true, confirmButtonText: 'Anular guia', cancelButtonText: 'Cancelar' });
      if (decision.isConfirmed) await runTransition('ANULADA', action.label, { description: `Anulacion desde detalle de guia. Motivo: ${decision.value}`, metadata: { source: 'guia_detalle', action: action.key, reason: decision.value, roleMode } });
    }
  }, [guia, guiaNumero, loadDetail, navigate, roleMode, runTransition]);

  const handlePreviewPdf = useCallback(async () => {
    try {
      if (guiaNumericId) await previewGuiaPdf(guiaNumericId);
    } catch (err) {
      await Swal.fire({
        title: 'SINA',
        text: err.message || 'No se pudo abrir el PDF de la guía.',
        icon: 'error',
        confirmButtonText: 'Entendido',
      });
    }
  }, [guiaNumericId]);
  const handleDownloadPdf = useCallback(async () => {
    try {
      if (guiaNumericId) await downloadGuiaPdf(guiaNumericId);
    } catch (err) {
      await Swal.fire('Error', err.message || 'No se pudo descargar el PDF de la guia.', 'error');
    }
  }, [guiaNumericId]);
  const handlePrintPdf = useCallback(async () => {
    try {
      if (guiaNumericId) await printGuiaPdf(guiaNumericId);
    } catch (err) {
      await Swal.fire({
        title: 'SINA',
        text: err.message || 'No se pudo imprimir la guía.',
        icon: 'error',
        confirmButtonText: 'Entendido',
      });
    }
  }, [guiaNumericId]);
  const goToCobranza = useCallback(() => { navigate('/tesoreria/cobranzas/nueva', { state: { guiaId: guia?.id, guiaNumero, fromGuiaDetail: true } }); }, [guia?.id, guiaNumero, navigate]);
  const goToFacturacion = useCallback(() => { navigate('/facturacion/nueva', { state: { guiaId: guia?.id, guiaNumero, fromGuiaDetail: true } }); }, [guia?.id, guiaNumero, navigate]);

  if (loading) return <div className="min-h-screen bg-[rgb(var(--bg))] px-4 py-6 text-[rgb(var(--fg))] md:px-6"><div className="mx-auto flex max-w-[1400px] items-center justify-center rounded-2xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.9)] p-12"><div className="flex items-center gap-3 text-sm text-[rgba(var(--fg),0.72)]"><Loader2 className="h-5 w-5 animate-spin" />Cargando guia...</div></div></div>;
  if (error || !guia) return <div className="min-h-screen bg-[rgb(var(--bg))] px-4 py-6 text-[rgb(var(--fg))] md:px-6"><div className="mx-auto max-w-[1400px] rounded-2xl border border-[rgba(var(--danger),0.3)] bg-[rgba(var(--danger),0.1)] p-8"><p className="text-lg font-semibold">No pudimos abrir la guia.</p><p className="mt-2 text-sm">{error || 'La guia solicitada no existe o no esta disponible.'}</p><Button variant="outline" className="mt-5 rounded-xl" onClick={loadDetail}>Reintentar</Button></div></div>;

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,rgba(var(--bg),1),rgba(var(--subpanel),0.56))] px-4 pb-5 pt-2 text-[rgb(var(--fg))] md:px-5" data-qa-screen="guia-detalle">
      <div className="mx-auto max-w-[1400px] space-y-4">
        <GuiaHeaderOperativo
          guiaNumero={guiaNumero}
          operationalState={operationalState}
          routeLabel={routeLabel}
          emissionDate={formatDate(guia.fechaEmision || guia.fecha_emision, { dateOnly: true })}
          compact={compactLayout}
          viaLabel={guia?.sucursalVia?.nombre || guia?.routing_mode || null}
          primaryAction={primaryAction}
          onPrimaryAction={handleAction}
          onBack={() => navigate(-1)}
          onRefresh={loadDetail}
          onPreviewPdf={handlePreviewPdf}
          onPrintPdf={handlePrintPdf}
          onDownloadPdf={handleDownloadPdf}
          onViewHistory={() => setActiveTab('historial')}
          extraActions={menuActions}
          onExtraAction={handleAction}
          canRevalorizar={canRevalorizar}
          onOpenRevalorizar={() => setRevalorizacionOpen(true)}
          transitioningKey={transitioningKey}
        />

        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 xl:col-span-8 space-y-4">
            <GuiaPrimerPliegue
              guia={guia}
              operationalState={operationalState}
              timelinePreview={timelinePreview}
              onViewHistory={() => setActiveTab('historial')}
              clientName={clientName}
              paymentConditionLabel={paymentConditionLabel}
              remitente={{ display: remitenteNombre, domicilio: remitenteDomicilio, localidad: remitenteLocalidad, telefono: remitenteTelefono }}
              destinatario={{ display: destinatarioNombre, domicilio: destinatarioDomicilio, localidad: destinatarioLocalidad, telefono: destinatarioTelefono }}
              compact={compactLayout}
            />
            <GuiaRemitosDestacados remitos={detail.remitos} compact={compactLayout} />
            <GuiaTabsDetalle activeTab={activeTab} setActiveTab={setActiveTab} guia={guia} detail={detail} commercialStatus={commercialStatus} collectionStatus={collectionStatus} billingStatus={billingStatus} currentTariffSnapshot={currentTariffSnapshot} valuationRows={valuationRows} valuationSnapshot={valuationSnapshot} facturaRelacion={facturaRelacion} goToCobranza={goToCobranza} goToFacturacion={goToFacturacion} navigate={navigate} operationalState={operationalState} timelineSucursalId={Number(sucursal?.id || detail.sucursalActual?.id || guia?.sucursalActual?.id || 0)} revaluationHistory={revaluationHistory} canRevalorizar={canRevalorizar} revaluationReason={revaluationReason} onOpenRevalorizar={() => setRevalorizacionOpen(true)} compact={compactLayout} stickyTopClass={stickyTopClass} />
            {guiaRetornables.length ? (
              <section className="rounded-2xl border border-[rgba(var(--border),0.55)] bg-[rgb(var(--panel))] p-4">
                <div className="flex items-center justify-between gap-2"><h2 className="text-sm font-semibold">Retornables</h2><Button variant="ghost" size="sm" onClick={() => navigate(`/cargas/retornables?guiaId=${guiaNumericId}`)}>Ver movimientos</Button></div>
                <div className="mt-2 space-y-1.5">{guiaRetornables.map(item => <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-[rgba(var(--subpanel),0.55)] px-3 py-2 text-xs"><span><strong>{item.cantidad}</strong> {item.tipoNombre || 'retornable(s)'} · {item.sucursalNombre || 'Sucursal operativa'}</span><span className="rounded-full border border-[rgba(var(--border),0.5)] px-2 py-1">{item.movimiento.replaceAll('_', ' ')}</span></div>)}</div>
              </section>
            ) : null}
          </div>

          <div className="col-span-12 xl:col-span-4">
            <GuiaPanelClave guia={guia} detail={detail} nextAction={nextAction} primaryAction={primaryAction} onPrimaryAction={handleAction} transitioningKey={transitioningKey} billingStatus={billingStatus} collectionStatus={collectionStatus} timelinePreview={timelinePreview} operationalState={operationalState} alerts={operationalAlerts} compact={compactLayout} stickyTopClass={stickyTopClass} />
          </div>
        </div>

      </div>
      <RevalorizacionDrawer open={revalorizacionOpen} onClose={() => setRevalorizacionOpen(false)} guia={guia} snapshotBundle={snapshotBundle} tenant={tenant} token={token} onApplied={loadDetail} />
    </div>
  );
}


